import React from 'react';
import { Link } from 'react-router-dom';
import { Cpu, ArrowLeft, CheckCircle, Users, Car, Filter, Database, Zap } from 'lucide-react';
import QuoteForm from '../components/QuoteForm';

const TechIAPage = () => {
  const images = {
    aiView: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fcameras_hlvf_ai_view_0dc05637cd%401.jpg",
    typeEvents: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Ftype_events_97f9c2bdb7%402.png",
    jetSparrow: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fjet_sparrow_md_3341c29cb1%402.jpg",
    filters: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Ffilters_30fe156c2d%402.jpg",
    calendar: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fcalendar_3f1407d5f6%402.jpg",
    export: "https://ajax.systems/api/cdn-img/?img=%2Fupload%2Fexport_a9071e6c40%402.jpg",
  };

  const aiFeatures = [
    { icon: Users, title: "Détection de personnes", desc: "L'IA distingue les humains des autres sources de mouvement (animaux, véhicules, ombres)" },
    { icon: Car, title: "Reconnaissance véhicules", desc: "Identification automatique des voitures, motos et camions dans le champ de vision" },
    { icon: Filter, title: "Zones de détection", desc: "Définissez des zones spécifiques pour recevoir des alertes ciblées uniquement" },
    { icon: Database, title: "Enregistrement intelligent", desc: "Économisez de l'espace en n'enregistrant que les événements pertinents" },
  ];

  return (
    <div className="min-h-screen py-8 md:py-12" data-testid="tech-ia-page">
      {/* Breadcrumb */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-12 mb-8">
        <Link to="/superior-video" className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span>Retour à la vidéosurveillance</span>
        </Link>
      </div>

      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-12">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-500/20 border border-purple-500/40 rounded-full mb-4">
              <Cpu className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-purple-400 font-semibold">Matériel 4× plus puissant</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase mb-4">
              Intelligence <span className="text-purple-400">Artificielle</span>
            </h1>
            <p className="text-lg text-white/50 mb-6 leading-relaxed">
              Les caméras Superior embarquent une IA 4 fois plus puissante pour identifier avec précision 
              les personnes, animaux et véhicules. Notifications ciblées, enregistrement intelligent.
            </p>
            <a href="#devis" className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-500/90 text-black font-semibold rounded-lg transition-colors">
              Demander un devis
            </a>
          </div>
          <div className="rounded-xl overflow-hidden">
            <img src={images.aiView} alt="Vue IA Ajax" className="w-full h-auto" />
          </div>
        </div>
      </section>

      {/* Section: Fonctionnalités IA */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-8 text-center">
          Voir l'essentiel avec la puissance de l'IA
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {aiFeatures.map((feature, i) => (
            <div key={i} className="bg-white/[0.02] border border-white/5 rounded-xl p-6 hover:border-purple-500/40 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-purple-400" />
              </div>
              <h4 className="text-white font-semibold mb-2">{feature.title}</h4>
              <p className="text-sm text-white/50">{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Section: Types d'événements */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="bg-gradient-to-br from-purple-900/30 to-fgs-surface border border-purple-500/20 rounded-2xl p-6 md:p-10">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                Notifications intelligentes
              </h2>
              <p className="text-white/50 mb-6 leading-relaxed">
                Recevez uniquement les alertes qui comptent. L'IA filtre les faux positifs 
                et vous notifie uniquement lorsqu'un événement pertinent est détecté.
              </p>
              <ul className="space-y-3">
                {[
                  "Filtrage par type d'objet (personne, véhicule, animal)",
                  "Zones de détection personnalisables",
                  "Horaires de notification configurables",
                  "Réduction drastique des fausses alertes",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0" />
                    <span className="text-white text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl overflow-hidden">
              <img src={images.typeEvents} alt="Types d'événements IA" className="w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Section: JetSparrow */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className="rounded-xl overflow-hidden">
            <img src={images.jetSparrow} alt="Technologie JetSparrow" className="w-full h-auto" />
          </div>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Zap className="w-8 h-8 text-purple-400" />
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase">
                Technologie JetSparrow
              </h2>
            </div>
            <p className="text-white/50 mb-6 leading-relaxed">
              Navigation ultra-rapide dans les archives. Grâce à notre technologie propriétaire, 
              le système réagit instantanément pour une lecture fluide et une recherche efficace.
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div className="rounded-lg overflow-hidden">
                <img src={images.filters} alt="Filtres" className="w-full h-24 object-cover" />
                <p className="text-xs text-center text-white/50 py-2 bg-white/[0.02]">Filtres IA</p>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img src={images.calendar} alt="Calendrier" className="w-full h-24 object-cover" />
                <p className="text-xs text-center text-white/50 py-2 bg-white/[0.02]">Calendrier</p>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img src={images.export} alt="Export" className="w-full h-24 object-cover" />
                <p className="text-xs text-center text-white/50 py-2 bg-white/[0.02]">Export rapide</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Formulaire */}
      <section id="devis" className="max-w-[1400px] mx-auto px-4 md:px-12 scroll-mt-24">
        <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6 md:p-10">
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                Passez à l'IA Superior
              </h2>
              <p className="text-white/50 mb-6">
                Toutes les caméras Superior intègrent l'IA enrichie pour une détection précise et des alertes ciblées.
              </p>
              <div className="space-y-3">
                {[
                  "IA 4× plus puissante que la gamme Baseline",
                  "Détection personnes, véhicules, animaux",
                  "Navigation JetSparrow ultra-rapide",
                  "Garantie 4 ans FGS",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-white text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <QuoteForm category="video" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default TechIAPage;
